"use client";

import { useEffect, useState, useMemo } from "react";
import { motion } from "motion/react";
import { SparklesCore } from "../../../../shared/animations/sparkles/soarklesCore";

/** Typewriter stays the same (resets whenever `text` changes) */
function TypewriterText({
  text,
  speed = 55,
  delay = 150,
  loop = false,
  className = "",
  cursor = true,
}) {
  const [i, setI] = useState(0);

  useEffect(() => {
    let startT, stepI, resetT;
    const start = () => {
      stepI = setInterval(() => {
        setI((prev) => {
          if (prev < text.length) return prev + 1;
          if (loop) {
            clearInterval(stepI);
            resetT = setTimeout(() => {
              setI(0);
              start();
            }, 900);
          }
          return prev;
        });
      }, speed);
    };
    startT = setTimeout(start, delay);
    return () => {
      clearTimeout(startT);
      clearInterval(stepI);
      clearTimeout(resetT);
    };
  }, [text, speed, delay, loop]);

  const shown = text.slice(0, i);
  const done = i >= text.length && !loop;

  return (
    <p dir="rtl" className={className} aria-label={text}>
      {shown}
      {cursor && !done && (
        <motion.span
          className="inline-block align-baseline"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 0.9, repeat: Infinity, ease: "linear" }}
          style={{
            width: "0.06em",
            height: "0.9em",
            marginInlineStart: "0.15em",
            backgroundColor: "#ffffff",
            borderRadius: 2,
          }}
        />
      )}
    </p>
  );
}

/**
 * WelcomeComponent
 * - If `text` prop is provided, use it with typewriter.
 * - Otherwise, fallback to the default Persian message.
 * - You can still tweak speed/delay/loop from parent if needed.
 */
export function WelcomeComponent({
  text,
  speed = 55,
  delay = 150,
  loop = false,
  cursor = true,
}) {
  const defaultText = "به خانه معامله‌گران خوش آمدید";
  const displayText = useMemo(
    () => (typeof text === "string" && text.trim() ? text : defaultText),
    [text]
  );

  return (
    <div className="w-full h-full flex items-center justify-center flex-col gap-[19px] relative ">
      <SparklesCore
        id="tsparticlesfullpage"
        background="transparent"
        minSize={0.6}
        maxSize={1.4}
        particleDensity={80}
        className="w-[450px] h-[600px] absolute top-[-100px] left-[50%] translate-x-[-50%] rotate-[45deg] rounded-[40%] overflow-hidden "
        particleColor="#FFFFFF"
      />

      <div
        style={{
          backgroundImage:
            "radial-gradient(ellipse 60% 35% at 50% 50%, rgba(240,48,0,0.48) -25%, rgba(240,48,0,0) 70%)",
          backdropFilter: "blur(0)",
          WebkitBackdropFilter: "blur(0)",
          borderRadius: "16px",
        }}
        className="w-[1000px] h-[1000px] absolute z-[3]"
      />

      <div className="w-[1000px] flex flex-col items-center justify-center overflow-hidden rounded-md">
        <TypewriterText
          text={displayText}
          className="text-[#FFFFFF] text-[57px] font-[900] mt-[120px] relative z-30"
          speed={speed}
          delay={delay}
          loop={loop}
          cursor={cursor}
        />

        <div className="relative mt-10 h-40 w-[40rem]">
          <div className="absolute inset-x-20 top-0 h-[3px] w-3/4 bg-gradient-to-r from-transparent via-white/70 to-transparent blur-md" />
          <div className="absolute inset-x-20 top-0 h-px w-3/4 bg-gradient-to-r from-transparent via-white to-transparent drop-shadow-[0_0_8px_#ffffff]" />
          <div className="absolute inset-x-60 top-0 h-[6px] w-1/4 bg-gradient-to-r from-transparent via-white/80 to-transparent blur-md" />
          <div className="absolute inset-x-60 top-0 h-px w-1/4 bg-gradient-to-r from-transparent via-white to-transparent drop-shadow-[0_0_10px_#ffffff]" />
          <div className="absolute inset-x-44 top-0 h-[2px] w-2/4 bg-gradient-to-r from-transparent via-sky-100/60 to-transparent blur-[2px]" />
          <div className="pointer-events-none absolute inset-0 h-full w-full [mask-image:radial-gradient(350px_200px_at_top,transparent_20%,white)]" />
        </div>
      </div>
    </div>
  );
}

export default WelcomeComponent;
